import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import trackUtil from '@/utils/analytics-service';
import { OnboardingPath, PageViewedProps } from '@/utils/analytics/types';
import type { DataSourceTypeValue, EntryContext, JourneyName, JourneyStep, PageName } from '@/utils/analytics/taxonomy';
import { sanitizePagePath } from '@/utils/analytics/taxonomy';
import { useOrgContext } from '@/contexts/orgContext';

// page_viewed intentionally carries only page_name, tab_selected, user_id, and organization_id
// (plus journey context). Do not add workgroup_id/workspace_id/database_id/graph_id here.
type UsePageViewedOptions = {
  page_name: PageName;
  tab_selected?: string | null;
  journey_name?: JourneyName;
  journey_step?: JourneyStep;
  entry_context?: EntryContext;
  onboarding_path?: OnboardingPath;
  page_path?: string;
  preloaded_solution_id?: string;
  tutorial_prompt_shown?: boolean;
  time_since_path_selected_seconds?: number;
  ingestion_flow_id?: string;
  data_source_type?: DataSourceTypeValue;
  user_id?: string;
  organization_id?: string;
  enabled?: boolean;
  waitForUser?: boolean;
  waitForOrg?: boolean;
};

let previousPageName: string | undefined;
let hasTrackedSignupPageView = false;

// entry_context describes how the user reached a destination. An explicit value always
// wins — an onboarding hand-off knows something the browser cannot. Otherwise it is read
// from navigation history and the Navigation Timing type, never from the URL's contents:
// a previous in-app page means the user used a navigation control, no previous page on a
// reload means a refresh, and no previous page on a deep path means a bookmark or shared
// link rather than someone typing the origin.
function resolveEntryContext(explicit: EntryContext | undefined, pagePath: string | undefined): EntryContext {
  if (explicit) {
    return explicit;
  }
  if (previousPageName) {
    return 'workspace_navigation';
  }
  if (isReloadNavigation()) {
    return 'refresh';
  }
  return pagePath && pagePath !== '/' ? 'deep_link' : 'direct_navigation';
}

function isReloadNavigation(): boolean {
  try {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return navigation?.type === 'reload';
  } catch {
    return false;
  }
}

// page_path is part of the key because React Router keeps the same component mounted
// when only a route param changes. Without it, navigating from one workgroup to another
// — or between workspaces in the query editor — fires no page_viewed at all.
function getPageViewKey(pageName: string, tabSelected: string | null, journeyStep?: string, pagePath?: string) {
  return `${pageName}:${tabSelected ?? 'null'}:${journeyStep ?? 'null'}:${pagePath ?? 'null'}`;
}

export function resetPageViewState() {
  previousPageName = undefined;
  lastFiredDedupeKey = null;
  lastFiredAt = 0;
  hasTrackedSignupPageView = false;
}

// Per-hook-instance refs can't dedupe across a remount (e.g. a route/component that unmounts
// and immediately remounts, such as during a redirect-driven navigation), because a fresh
// mount gets a fresh ref. Keep a short, module-level dedupe window as a second line of defense
// so an accidental rapid remount can't double-fire the exact same page_viewed event.
const RAPID_REFIRE_WINDOW_MS = 2000;
let lastFiredDedupeKey: string | null = null;
let lastFiredAt = 0;

type TrackPageViewedOptions = {
  // Flush the Mixpanel queue immediately after tracking. Use before a hard navigation
  // (e.g. Auth0 redirect) where the page may unload before the default batch send.
  flush?: boolean;
  // Skip the short cross-remount dedupe window (e.g. pre-auth pages that only fire once).
  skipDedupe?: boolean;
};

export function trackPageViewedEvent(props: PageViewedProps, options?: TrackPageViewedOptions) {
  const tabSelected = props.tab_selected ?? null;
  // Identity fields are intentionally excluded: the same visible page should dedupe even if
  // user_id/organization_id resolve a moment later on first paint.
  const dedupeKey = getPageViewKey(props.page_name, tabSelected, props.journey_step, props.page_path);

  const now = Date.now();
  if (!options?.skipDedupe && lastFiredDedupeKey === dedupeKey && now - lastFiredAt < RAPID_REFIRE_WINDOW_MS) {
    return;
  }
  lastFiredDedupeKey = dedupeKey;
  lastFiredAt = now;

  const payload: PageViewedProps = {
    ...props,
    tab_selected: tabSelected,
    previous_page_name: previousPageName,
  };

  trackUtil.trackPageViewed(payload, options?.flush ? { flush: true } : undefined);
  previousPageName = props.page_name;
}

/** Fire signup page_viewed once per session; flush before Auth0 redirect. */
export function trackSignupPageViewed() {
  if (hasTrackedSignupPageView) {
    return;
  }
  hasTrackedSignupPageView = true;

  trackPageViewedEvent(
    {
      page_name: 'signup',
      tab_selected: null,
      journey_name: 'account_creation',
      journey_step: 'signup',
    },
    { flush: true, skipDedupe: true }
  );
}

/** Pre-authentication account-creation pages (no user_id / organization_id). */
export function trackEmailVerificationPageViewed() {
  trackPageViewedEvent(
    {
      page_name: 'email_verification',
      tab_selected: null,
      journey_name: 'account_creation',
      journey_step: 'email_verification',
    },
    { skipDedupe: true }
  );
}

export function usePageViewed({
  page_name,
  tab_selected = null,
  journey_name,
  journey_step,
  entry_context,
  onboarding_path,
  page_path,
  preloaded_solution_id,
  tutorial_prompt_shown,
  time_since_path_selected_seconds,
  ingestion_flow_id,
  data_source_type,
  user_id,
  organization_id,
  enabled = true,
  waitForUser = true,
  waitForOrg = true,
}: UsePageViewedOptions) {
  const { userInfo, currentOrg } = useOrgContext();
  const location = useLocation();
  const lastTrackedKeyRef = useRef<string | null>(null);
  const resolvedPagePath = page_path ?? sanitizePagePath(location.pathname);

  useEffect(() => {
    if (!enabled) {
      const dedupeKey = getPageViewKey(page_name, tab_selected, journey_step, resolvedPagePath);
      if (lastFiredDedupeKey === dedupeKey) {
        lastFiredDedupeKey = null;
        lastFiredAt = 0;
      }
      lastTrackedKeyRef.current = null;
      return;
    }

    const resolvedUserId = user_id ?? userInfo?.id;
    const resolvedOrganizationId = organization_id ?? currentOrg?.org_id;

    if (waitForUser && !resolvedUserId) {
      return;
    }
    if (waitForOrg && !resolvedOrganizationId) {
      return;
    }

    const pageViewKey = getPageViewKey(page_name, tab_selected, journey_step, resolvedPagePath);
    if (lastTrackedKeyRef.current === pageViewKey) {
      return;
    }

    trackPageViewedEvent({
      page_name,
      tab_selected,
      journey_name,
      journey_step,
      entry_context: resolveEntryContext(entry_context, resolvedPagePath),
      onboarding_path,
      page_path: resolvedPagePath,
      preloaded_solution_id,
      tutorial_prompt_shown,
      time_since_path_selected_seconds,
      ingestion_flow_id,
      data_source_type,
      user_id: resolvedUserId,
      organization_id: resolvedOrganizationId,
    });
    lastTrackedKeyRef.current = pageViewKey;
  }, [
    currentOrg?.org_id,
    enabled,
    entry_context,
    journey_name,
    journey_step,
    onboarding_path,
    resolvedPagePath,
    preloaded_solution_id,
    tutorial_prompt_shown,
    time_since_path_selected_seconds,
    ingestion_flow_id,
    data_source_type,
    organization_id,
    page_name,
    tab_selected,
    user_id,
    userInfo?.id,
    waitForOrg,
    waitForUser,
  ]);
}
